import { useEffect, useMemo, useRef, useState } from "react";

import { Empty, message, Modal as AntModal } from "antd";
import { CloseOutlined, SearchOutlined, UploadOutlined } from "@ant-design/icons";

import { Button, Input, Select } from "../../../../shared";
import { API_ROUTES } from "../../../../../api/ApiRoutes";
import RebetLoader from "../../../../CommonComponents/RebetLoader/RebetLoader";
import {
  type AdminCollection,
  useCollectionsByKind,
} from "../../../../../queries/casino-configuration/collections/useAdminCollections";
import { useCreateAdminCollection } from "../../../../../queries/casino-configuration/collections/useSaveAdminCollection";
import { getAuthedInstance } from "../../../../../queries/casino-configuration/collections/useCollectionGames";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { formatString } from "../../../../../utils/constants/stringUtils";
import { getApiErrorMessage } from "../../../../../utils/ErrorUtils";
import { useCurationDisplayName } from "../../../../../hooks/useCurationDisplayName";
import CurationEntityName from "../../CurationEntityName";
import {
  COLLECTION_ICON_ACCEPT,
  COLLECTION_MAX_ICON_BYTES,
  getCollectionDefaultViewOptions,
  isAcceptedCollectionIcon,
} from "../collectionHelpers";

import "../stylesCollectionsConfiguration.css";

type Kind = "games" | "providers";

const SEARCH_DEBOUNCE_MS = 250;
const PARENT_PICKER_PAGE_SIZE = 50;
/**
 * Mirrors the backend slug constraint on collection codes: start with a
 * lowercase alphanumeric, then any of lowercase alphanumeric, underscore,
 * or hyphen. Wrong-shape codes get a cryptic API error if we let them
 * through, so we gate the Create button silently instead.
 */
const COLLECTION_CODE_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;

type ParentPickerProps = {
  open: boolean;
  kind: Kind;
  selectedCode: string | null;
  /**
   * Called with a parent code when the user picks an actual collection, or
   * with `null` when they pick "Top-level" (which means "don't nest").
   */
  onSelect: (code: string | null) => void;
  onClose: () => void;
};

function ParentPickerModal({
  open,
  kind,
  selectedCode,
  onSelect,
  onClose,
}: ParentPickerProps) {
  const { getName } = useCurationDisplayName();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (open) {
      setSearch("");
      setDebouncedSearch("");
    }
  }, [open]);

  // Fetch a bounded page of collections of the same kind. We deliberately
  // do NOT pass `exclude_code` here because the new collection has no code
  // on the backend yet — passing a fake one returns 404 "exclude_code does
  // not exist". For large catalogs the user narrows via search; the
  // backend honours the same `search` param on the server side.
  const { data, isLoading } = useCollectionsByKind(
    open ? kind : null,
    debouncedSearch || undefined,
    1,
    PARENT_PICKER_PAGE_SIZE,
  );

  const items: AdminCollection[] = data?.items ?? [];

  return (
    <AntModal
      open={open}
      onCancel={onClose}
      footer={null}
      width={520}
      className="cd-add-modal nc-parent-modal"
      destroyOnHidden
      closeIcon={<CloseOutlined />}
      title={
        <div>
          <div className="cd-add-title">
            {AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_PARENT_TITLE}
          </div>
          <div className="cd-add-selected">
            {formatString(
              AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_PARENT_SUBTITLE,
              { kind },
            )}
          </div>
        </div>
      }
    >
      <Input
        className="cd-add-search"
        prefix={<SearchOutlined />}
        placeholder={AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_PARENT_SEARCH}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
      />

      <div className="cd-add-list nc-parent-list">
        {/*
          Top-level row is rendered as a plain "back out" option. It must
          never carry a "selected" badge while the user is inside this
          picker, because the picker is only opened when the user is trying
          to choose a real parent (Sub-collection toggle is ON). Picking
          this row turns the toggle off and clears any parentCode.
        */}
        <button
          type="button"
          className="nc-parent-row nc-parent-row--top"
          onClick={() => onSelect(null)}
        >
          <div className="nc-parent-row-text">
            <div className="nc-parent-row-name">
              {AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_PARENT_TOP_LEVEL}
            </div>
            <div className="nc-parent-row-hint">
              {
                AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_PARENT_TOP_LEVEL_HINT
              }
            </div>
          </div>
        </button>

        {isLoading ? (
          <div className="cd-games-loader">
            <RebetLoader height="32px" width="32px" />
          </div>
        ) : items.length === 0 ? (
          <Empty
            description={
              AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_PARENT_NO_RESULTS
            }
          />
        ) : (
          items.map((item) => {
            const isSelected = selectedCode === item.code;
            return (
              <button
                type="button"
                key={item.code}
                className={`nc-parent-row ${
                  isSelected ? "nc-parent-row--selected" : ""
                }`}
                onClick={() => onSelect(item.code)}
              >
                <div className="nc-parent-row-text">
                  <div className="nc-parent-row-name">
                    <CurationEntityName
                      entity={item}
                      displayName={getName(item)}
                    />
                  </div>
                  <div className="nc-parent-row-hint">{item.code}</div>
                </div>
                {isSelected && (
                  <span className="nc-parent-row-selected-label">
                    {AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_PARENT_SELECTED}
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>

      <div className="nc-parent-footer">
        <button
          type="button"
          className="cd-add-cancel-btn nc-parent-close-btn"
          onClick={onClose}
        >
          {AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_PARENT_CLOSE}
        </button>
      </div>
    </AntModal>
  );
}

type NewCollectionModalProps = {
  open: boolean;
  onClose: () => void;
  canUploadImages?: boolean;
};

export default function NewCollectionModal({
  open,
  onClose,
  canUploadImages = true,
}: NewCollectionModalProps) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState<Kind>("games");
  const [defaultView, setDefaultView] = useState<string>("all");
  const [iconUrl, setIconUrl] = useState("");
  const [isActive, setIsActive] = useState(true);
  // Always defaults to off; admins explicitly opt in via the Animate Icon
  // toggle. Spec requires this default for both create and edit flows.
  const [isAnimated, setIsAnimated] = useState(false);
  const [isSubcollection, setIsSubcollection] = useState(false);
  const [parentCode, setParentCode] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [parentPickerOpen, setParentPickerOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutateAsync: createCollection, isPending: creating } =
    useCreateAdminCollection();

  useEffect(() => {
    if (open) {
      setCode("");
      setName("");
      setKind("games");
      setDefaultView("all");
      setIconUrl("");
      setIsActive(true);
      setIsAnimated(false);
      setIsSubcollection(false);
      setParentCode(null);
      setUploading(false);
      setParentPickerOpen(false);
    }
  }, [open]);

  // Providers collections can't nest in this design.
  useEffect(() => {
    if (kind !== "games" && isSubcollection) {
      setIsSubcollection(false);
      setParentCode(null);
    }
  }, [kind, isSubcollection]);

  const trimmedCode = code.trim();
  const trimmedName = name.trim();

  const canCreate = useMemo(() => {
    if (!trimmedCode || !trimmedName) return false;
    if (!COLLECTION_CODE_PATTERN.test(trimmedCode)) return false;
    if (isSubcollection && !parentCode) return false;
    return true;
  }, [trimmedCode, trimmedName, isSubcollection, parentCode]);

  const handleFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!canUploadImages) {
      message.error(AppStrings.PAM_UPLOADS_PERMISSION_REQUIRED);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    if (!isAcceptedCollectionIcon(file)) {
      message.error(
        AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_FILE_TYPE_INVALID,
      );
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (file.size > COLLECTION_MAX_ICON_BYTES) {
      message.error(AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_FILE_TOO_LARGE);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (!trimmedCode) {
      message.error(AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_CODE_REQUIRED);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setUploading(true);
    try {
      const instance = getAuthedInstance();
      const { data: presignedResp } = await instance.post(
        API_ROUTES.ADMIN_UPLOADS_PRESIGNED,
        {
          image_type: "collection_icon",
          identifier: trimmedCode,
          content_type: file.type || "image/png",
          existing_url: iconUrl || null,
        },
      );
      const presigned = presignedResp?.data ?? presignedResp;
      const uploadUrl = presigned?.upload_url;
      if (!uploadUrl) {
        throw new Error(AppStrings.CASINO_CONFIG_COLLECTIONS_PRESIGNED_URL_FAILED);
      }

      await fetch(uploadUrl, {
        method: presigned?.http_method || "PUT",
        headers: { "Content-Type": file.type || "image/png" },
        body: file,
      });

      const newUrl: string | undefined = presigned?.cloudfront_url;
      if (newUrl) {
        const cacheBust = `${newUrl}${
          newUrl.includes("?") ? "&" : "?"
        }t=${Date.now()}`;
        setIconUrl(cacheBust);
      }
    } catch (err: unknown) {
      message.error(
        getApiErrorMessage(
          err,
          (err as Error)?.message ||
            AppStrings.CASINO_CONFIG_COLLECTIONS_UPLOAD_FAILED,
        ),
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleCreate = async (): Promise<void> => {
    if (!canCreate || creating) return;
    try {
      const cleanIconUrl = iconUrl ? iconUrl.split("?")[0] : null;
      await createCollection({
        code: trimmedCode,
        name: trimmedName,
        kind,
        default_view: defaultView,
        icon_url: cleanIconUrl,
        parent_code: isSubcollection ? parentCode : null,
        is_active: isActive,
        sibling_order: null,
        is_animated: isAnimated,
      });
      message.success(AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_CREATED);
      onClose();
    } catch (err: unknown) {
      message.error(
        getApiErrorMessage(
          err,
          (err as Error)?.message ||
            AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_CREATE_FAILED,
        ),
      );
    }
  };

  const subtitle = isSubcollection && parentCode
    ? formatString(
        AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_SUBTITLE_NESTED,
        { parent: parentCode },
      )
    : AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_SUBTITLE_TOP_LEVEL;

  return (
    <AntModal
      open={open}
      onCancel={onClose}
      width={720}
      className="cd-add-modal nc-modal"
      destroyOnHidden
      maskClosable={!creating && !uploading}
      closeIcon={<CloseOutlined />}
      title={
        <div>
          <div className="cd-add-title">
            {AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_TITLE}
          </div>
          <div className="cd-add-selected">{subtitle}</div>
        </div>
      }
      footer={
        <div className="nc-footer">
          <button
            type="button"
            className="nc-cancel-btn"
            onClick={onClose}
            disabled={creating || uploading}
          >
            {AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_CANCEL}
          </button>
          <Button
            intent="primary"
            className="nc-create-btn"
            disabled={!canCreate || creating || uploading}
            loading={creating}
            onClick={handleCreate}
          >
            {creating
              ? AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_CREATING
              : AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_CREATE}
          </Button>
        </div>
      }
    >
      <div className="nc-body">
        <div className="nc-row">
          <div className="nc-field">
            <label htmlFor="nc-code" className="nc-label">
              {AppStrings.CASINO_CONFIG_COLLECTIONS_CODE_LABEL} *
            </label>
            <Input
              id="nc-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={
                AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_CODE_PLACEHOLDER
              }
              className="nc-input"
              data-testid="nc-code-input"
            />
          </div>
          <div className="nc-field">
            <label className="nc-label">
              {AppStrings.CASINO_CONFIG_COLLECTIONS_KIND_LABEL} *
            </label>
            <Select
              value={kind}
              onChange={(value: string) => setKind(value as Kind)}
              className="nc-select"
              options={[
                {
                  value: "games",
                  label:
                    AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_KIND_GAMES,
                },
                {
                  value: "providers",
                  label:
                    AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_KIND_PROVIDERS,
                },
              ]}
            />
          </div>
        </div>

        <div className="nc-field nc-field--default-view">
          <label className="nc-label">
            {AppStrings.CASINO_CONFIG_COLLECTIONS_DEFAULT_VIEW_LABEL}
          </label>
          <Select
            value={defaultView}
            onChange={(value: string) => setDefaultView(value)}
            className="nc-select"
            options={getCollectionDefaultViewOptions()}
          />
          <span className="nc-hint">
            {AppStrings.CASINO_CONFIG_COLLECTIONS_DEFAULT_VIEW_HINT}
          </span>
        </div>

        <div className="nc-field">
          <label htmlFor="nc-name" className="nc-label">
            {AppStrings.CASINO_CONFIG_COLLECTIONS_NAME_LABEL} *
          </label>
          <Input
            id="nc-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={
              AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_NAME_PLACEHOLDER
            }
            className="nc-input"
            data-testid="nc-name-input"
          />
        </div>

        <div className="nc-field">
          <label className="nc-label">
            {AppStrings.CASINO_CONFIG_COLLECTIONS_ICON_LABEL}
          </label>
          <div className="nc-icon-row">
            {iconUrl ? (
              <img
                className="nc-icon-preview"
                src={iconUrl}
                alt=""
                data-testid="nc-icon-preview"
              />
            ) : (
              <div className="nc-icon-placeholder" aria-hidden />
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={COLLECTION_ICON_ACCEPT}
              style={{ display: "none" }}
              data-testid="nc-icon-file-input"
              onChange={handleFileSelect}
            />
            <Button
              intent="neutral"
              className="nc-upload-btn"
              iconLeft={<UploadOutlined />}
              loading={uploading}
              disabled={!canUploadImages || !trimmedCode || uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_ICON_UPLOAD}
            </Button>
            <span className="nc-hint nc-hint--inline">
              {trimmedCode
                ? AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_ICON_HINT_AFTER
                : AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_ICON_HINT_BEFORE}
            </span>
          </div>
        </div>

        <div className="nc-card">
          <div className="nc-card-row">
            <div>
              <div className="nc-card-title">
                {AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_ANIMATE_ICON_TITLE}
              </div>
              <div className="nc-card-hint">
                {AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_ANIMATE_ICON_HINT}
              </div>
            </div>
            <label className="cd-toggle-switch">
              <input
                type="checkbox"
                checked={isAnimated}
                onChange={(e) => setIsAnimated(e.target.checked)}
                data-testid="nc-animate-icon-toggle"
                aria-label={
                  AppStrings
                    .CASINO_CONFIG_COLLECTIONS_NEW_ANIMATE_ICON_TITLE
                }
              />
              <span className="cd-toggle-slider" />
            </label>
          </div>
        </div>

        {kind === "games" && (
          <div className="nc-card">
            <div className="nc-card-row">
              <div>
                <div className="nc-card-title">
                  {
                    AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_SUB_TOGGLE_TITLE
                  }
                </div>
                <div className="nc-card-hint">
                  {
                    AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_SUB_TOGGLE_HINT
                  }
                </div>
              </div>
              <label className="cd-toggle-switch">
                <input
                  type="checkbox"
                  checked={isSubcollection}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setIsSubcollection(next);
                    if (next) {
                      setParentPickerOpen(true);
                    } else {
                      setParentCode(null);
                    }
                  }}
                  aria-label={
                    AppStrings
                      .CASINO_CONFIG_COLLECTIONS_NEW_SUB_TOGGLE_TITLE
                  }
                />
                <span className="cd-toggle-slider" />
              </label>
            </div>

            {isSubcollection && (
              <div className="nc-parent-pill">
                <span className="nc-parent-pill-label">
                  {AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_SUB_PARENT_LABEL}{" "}
                  <code>{parentCode || "\u2014"}</code>
                </span>
                <div className="nc-parent-pill-actions">
                  <button
                    type="button"
                    className="nc-parent-change-btn"
                    onClick={() => setParentPickerOpen(true)}
                  >
                    {AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_SUB_CHANGE}
                    {" \u203a"}
                  </button>
                  <button
                    type="button"
                    className="nc-parent-clear-btn"
                    aria-label={AppStrings.CASINO_CONFIG_COLLECTIONS_CLEAR_PARENT_ARIA}
                    onClick={() => {
                      setParentCode(null);
                      setIsSubcollection(false);
                    }}
                  >
                    <CloseOutlined />
                  </button>
                </div>
              </div>
            )}
            {isSubcollection && !parentCode && (
              <span className="nc-error">
                {AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_PARENT_REQUIRED}
              </span>
            )}
          </div>
        )}

        <div className="nc-card">
          <div className="nc-card-row">
            <div className="nc-card-title">
              {AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_ACTIVE}
            </div>
            <label className="cd-toggle-switch">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                aria-label={AppStrings.CASINO_CONFIG_COLLECTIONS_NEW_ACTIVE}
              />
              <span className="cd-toggle-slider" />
            </label>
          </div>
        </div>
      </div>

      <ParentPickerModal
        open={parentPickerOpen}
        kind={kind}
        selectedCode={parentCode}
        onSelect={(picked) => {
          // The picker calls this when the user picks a row. It does NOT
          // also call `onClose`, so we own closing here. That avoids a
          // stale-closure bug where `onClose` ran with `parentCode`
          // pre-update and incorrectly reverted the Sub-collection toggle.
          setParentCode(picked);
          setParentPickerOpen(false);
          if (picked === null) {
            // User picked "Top-level" — they've chosen not to nest. Turn
            // the Sub-collection toggle off and exit the sub-collection
            // flow entirely.
            setIsSubcollection(false);
          }
        }}
        onClose={() => {
          setParentPickerOpen(false);
          // If the user dismissed the picker without selecting a parent,
          // revert the Sub-collection toggle so the empty `Parent: —`
          // pill and the "Pick a parent…" error don't appear.
          if (isSubcollection && !parentCode) {
            setIsSubcollection(false);
          }
        }}
      />
    </AntModal>
  );
}
